/**
 * CookieScript renders the "Show cookies" accordion in its full site details
 * (FSD) dialog without the attributes it needs: the control announces neither
 * its expanded state nor what it controls. The markup is third-party, so it can
 * only be fixed on the live DOM - and with an observer rather than a one-off
 * pass, because:
 *
 *  - the FSD dialog (#cookiescript_injected_fsd) is a direct child of <body>
 *    that the widget creates on open and REMOVES on close, so the node is new
 *    every time;
 *  - the toggle only adds/removes the `cookiescript_hidden` class on the panel
 *    and emits no event.
 *
 * Markup of a single category - the panel class is not unique, so we key off
 * the control and its next sibling:
 *
 *   <div class="cookiescript_fsd_category">
 *     <div class="cookiescript_fsd_category_main">…</div>
 *     <div role="button" tabindex="0" class="cookiescript_fsd_cookies_control">…</div>
 *     <div class="cookiescript_fsd_cookies cookiescript_hidden" inert>
 *       <table>… <a tabindex="-1" href="…">Vendor</a> …</table>
 *     </div>
 *   </div>
 *
 * The widget toggles `inert` together with the class, so a collapsed panel is
 * already out of the focus order and the accessibility tree. The vendor links,
 * however, carry a permanent tabindex="-1": once the panel is open they are
 * visible but unreachable by keyboard. The attribute is therefore dropped
 * while the panel is expanded and put back while it is collapsed.
 *
 * The same dialog holds the two tabs ("Cookie-Erklärung" / "Informationen zu
 * Cookies"). The widget already gives them role="tablist" and role="tab", but
 * nothing links a tab to its panel and nothing says which tab is selected:
 *
 *   <div class="cookiescript_fsd_tabs" role="tablist">
 *     <div id="cookiescript_declaration" role="tab" class="cookiescript_active">…</div>
 *     <div id="cookiescript_aboutcookies" role="tab">…</div>
 *   </div>
 *   <div class="cookiescript_fsd_tabs_content">
 *     <div id="cookiescript_declarationwrap">…</div>
 *     <div id="cookiescript_aboutwrap" class="cookiescript_hidden">…</div>
 *   </div>
 */

const FSD_ROOT_ID = 'cookiescript_injected_fsd';
const CONTROL_SELECTOR = '.cookiescript_fsd_cookies_control';
const HIDDEN_CLASS = 'cookiescript_hidden';
// What the widget renders inside a cookie panel that can take focus
const FOCUSABLE_SELECTOR = 'a[href], button';
const PANEL_ID = 'cookie-details';
const TABLIST_SELECTOR = '.cookiescript_fsd_tabs';
const ACTIVE_TAB_CLASS = 'cookiescript_active';
// Each tab and the panel it controls, keyed by the ids the widget renders
const TAB_PANELS: Record<string, string> = {
  cookiescript_declaration: 'cookiescript_declarationwrap',
  cookiescript_aboutcookies: 'cookiescript_aboutwrap',
};

let bodyObserver: MutationObserver | null = null;
let fsdObserver: MutationObserver | null = null;
let watchedRoot: HTMLElement | null = null;

// Write only when the value really changes, so the widget's own re-render stays
// the only thing that ever moves in this DOM
function setAttr(element: Element, name: string, value: string) {
  if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

function applyCookieListA11y() {
  Array.from(document.querySelectorAll(CONTROL_SELECTOR)).forEach((control, index) => {
    const panel = control.nextElementSibling;
    if (!panel) return;

    // There can be several categories, so the id has to stay unique
    if (!panel.id) panel.id = index === 0 ? PANEL_ID : `${PANEL_ID}-${index + 1}`;
    setAttr(control, 'aria-controls', panel.id);

    // The class the widget itself sets is the single source of truth
    const collapsed = panel.classList.contains(HIDDEN_CLASS);
    setAttr(control, 'aria-expanded', collapsed ? 'false' : 'true');
    Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).forEach((element) => {
      if (collapsed) setAttr(element, 'tabindex', '-1');
      else element.removeAttribute('tabindex');
    });
  });
}

function applyTabsA11y() {
  const tablist = document.querySelector(TABLIST_SELECTOR);
  if (tablist) setAttr(tablist, 'role', 'tablist');

  Object.entries(TAB_PANELS).forEach(([tabId, panelId]) => {
    const tab = document.getElementById(tabId);
    const panel = document.getElementById(panelId);
    if (!tab || !panel) return;

    setAttr(tab, 'role', 'tab');
    setAttr(tab, 'aria-controls', panelId);
    setAttr(panel, 'role', 'tabpanel');
    setAttr(panel, 'aria-labelledby', tabId);

    // Again the widget's own classes, not a copy of the state kept here
    setAttr(tab, 'aria-selected', tab.classList.contains(ACTIVE_TAB_CLASS) ? 'true' : 'false');
    // The inactive panel is only faded out (opacity: 0; height: 0), it is
    // neither `display: none` nor `visibility: hidden`, so without this its
    // text stays in the accessibility tree while the other tab is selected
    setAttr(panel, 'aria-hidden', panel.classList.contains(HIDDEN_CLASS) ? 'true' : 'false');
  });
}

function applyA11y() {
  applyCookieListA11y();
  applyTabsA11y();
}

function syncFsdRoot() {
  const root = document.getElementById(FSD_ROOT_ID);
  if (root === watchedRoot) return;

  fsdObserver?.disconnect();
  watchedRoot = root;
  if (!root) return;

  applyA11y();
  // Watch `class` only: our own aria-*/id writes never wake the observer,
  // so this cannot loop.
  fsdObserver = fsdObserver || new MutationObserver(applyA11y);
  fsdObserver.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
}

/**
 * Installed once, when CookieScript is attached. There is no need to wait for
 * the widget to initialise - the observer picks the FSD dialog up as soon as
 * the widget renders it.
 */
export function initCookieScriptA11y() {
  if (bodyObserver) return;

  syncFsdRoot();
  // The FSD dialog is added and removed as a direct child of <body>, no subtree needed
  bodyObserver = new MutationObserver(syncFsdRoot);
  bodyObserver.observe(document.body, { childList: true });
}

export function destroyCookieScriptA11y() {
  bodyObserver?.disconnect();
  fsdObserver?.disconnect();
  bodyObserver = null;
  fsdObserver = null;
  watchedRoot = null;
}
